// `import type` only — this module is deep-imported by the MSW handler, which every test in the
// suite loads. A value import from `@/components/common/chip` would drag React into that graph.
import type { ChipIntent } from '@/components/common/chip';

import type { WarrantyClaimReason, WarrantyClaimStatus } from './schemas';

/** Both Warranty listings page at 20 (ADR-0019: one `useInfiniteQuery`, one response shape). */
export const WARRANTY_POLICY = { PAGE_SIZE: 20 } as const;

/** Copy from the ACs verbatim — grilling Q9: the AC owns copy, Figma owns layout. */
export const WARRANTY_COPY = {
  CLAIMS_TITLE: 'Warranty Claims',
  NO_CLAIMS: 'No claims found',
  LOAD_FAILED: 'We could not load your warranty claims. Please try again.',
  RAISE_CTA: 'Raise Warranty Claim',
  FORM_TITLE: 'Claim Warranty',
  SUBMIT: 'Submit',
  DEVICE_LABEL: 'Select Device',
  REASON_LABEL: 'Reason for Warranty Claim',
  // Shared by **both** Select fields (Device and Reason): Figma gives them the same word, and one
  // constant beats two that can drift apart.
  SELECT_PLACEHOLDER: 'Select',
  DESCRIPTION_LABEL: 'Description',
  // 113319 verbatim. Figma 944:564547 reads "Enter"; the AC owns copy (ADR-0019).
  DESCRIPTION_PLACEHOLDER: 'Enter description',
  IMAGES_LABEL: 'Images',
  // Figma 1158:605206 has a trailing period; the AC does not, and the AC owns copy.
  RAISE_SUCCESS: 'Claim raised successfully',
  RAISE_FAILED: 'We could not submit your warranty claim. Please try again.',
  PREVIEW_TITLE: 'Ticket Preview',
  ACTIVITY_TIMELINE: 'Activity Timeline',
  // Figma 430:411897 and the ticket's AC agree on this one, unlike the admin twin, whose own AC
  // said "Rejection Reason".
  REQUEST_REJECTED: 'Request Rejected',
  CLAIM_LOAD_FAILED: 'We could not load this warranty claim. Please try again.',
  RETRY: 'Retry',
} as const;

/**
 * Claim status → `<Chip>`. Every colourway is an exact match for Figma `430:394247`:
 * Approved #A7F0BA/#3D5B34 = `approved`, Pending #FDDC69/#684E00 = `review`,
 * Rejected #FFDFDF/#A2191F = `attention`. No new chip styling is needed.
 */
export const CLAIM_STATUS_CHIP = {
  pending: { label: 'Pending', intent: 'review' },
  approved: { label: 'Approved', intent: 'approved' },
  rejected: { label: 'Rejected', intent: 'attention' },
} as const satisfies Record<WarrantyClaimStatus, { label: string; intent: ChipIntent }>;

/** 113319: "further typing is blocked at the limit, no error is shown (graceful)". */
export const WARRANTY_DESCRIPTION_MAX = 2000;

/**
 * The five predefined reasons, rendered. The wire token never reaches the screen: the Ticket
 * Preview's card heading is always `WARRANTY_REASONS[claim.reason]` — never Figma's "Device
 * Broken", which is sample data and is not one of the five (context.md #20; ADR-0019 inherits
 * ADR-0023's ruling). One casing stays ours. Insertion order is the order 113319 lists the
 * options, which is the order the intake form's dropdown offers them.
 */
export const WARRANTY_REASONS = {
  mechanical_or_electrical_failure: 'Mechanical or Electrical Failure',
  manufacturing_defects: 'Manufacturing Defects',
  performance_failure: 'Performance Failure',
  structural_flaws: 'Structural Flaws',
  others: 'Others',
} as const satisfies Record<WarrantyClaimReason, string>;

/**
 * Activity Timeline chip per entry (Figma 430:411870 / 411879 / 411888).
 *
 * `created` is blue — `Info/100` #CDECFF on `Info/700` #357EDD, an exact match for
 * `<Chip intent="published">`. Every other entry *is* a Claim status, so the three header chips are
 * reused verbatim rather than restated.
 */
export const ACTIVITY_CHIP = {
  created: { label: 'Created', intent: 'published' },
  ...CLAIM_STATUS_CHIP,
} as const satisfies Record<string, { label: string; intent: ChipIntent }>;

/**
 * The chip for one timeline entry.
 *
 * Widened to accept any wire token, because `timeline[].status` parses as a bare string: an
 * unrecognised one degrades to a neutral chip carrying the raw token, instead of crashing on
 * `undefined.intent` (ADR-0019).
 */
export function activityChip(status: string): { label: string; intent: ChipIntent } {
  const chips: Record<string, { label: string; intent: ChipIntent }> = ACTIVITY_CHIP;
  return chips[status] ?? { label: status, intent: 'neutral' };
}
